import { ApplicationConfig, ErrorHandler, provideZonelessChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideStore } from '@ngrx/store';
import { LogLevel, provideAuth } from 'angular-auth-oidc-client';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SplunkService } from '@nationwide/client-logging';
import { APICommonService } from '@nationwide/internet-servicing-angular-services';
import {
    provideInternetServicingPolicyRequests
} from '@nationwide/dgs-internet-servicing-policy-requests';
import {
    DistributionPartnerInformationServiceConfig,
    distributionPartnerInformationServiceConfigToken,
    DistributionPartnerInformationService
} from '@nationwide/internet-servicing-dpim-requests';
import { PolicyNumberFormatterPipe } from '@nationwide/internet-servicing-angular-pipes';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { ValidatorService } from './shared/services/validator.service';
import { RequestHeaderInterceptorService } from './tpi-services/interceptors/request-header-interceptor.service';
import { RequestResponseLoggingInterceptorService } from './tpi-services/interceptors/request-response-logging-interceptor.service';
import { RequestTimeoutInterceptorService } from './tpi-services/interceptors/request-timeout-interceptor.service';
import { LoggerService } from './tpi-services/logger/logger.service';
import { SessionService } from './tpi-services/tpi-session/session.service';
import { ThirdPartyInquiryErrorHandler } from './tpi-services/third-party-inquiry-error-handler';
import { updateSearchFieldsReducer } from './tpi-reducers/policysearch.reducer';
import { updateSelectedCompanyFieldsReducer } from './tpi-reducers/selected-company.reducer';
import { updateUserFieldsReducer } from './tpi-reducers/user-fields.reducer';
import { EcifService } from './service/ecif.service';
import { DistributionPartnerIndividualInfoService } from './shared/services/distribution-partner-individual-info/distribution-partner-individual-info.service';
import { EditLienholderService } from './tpi-services/shared-services/edit-lienholder.service';
import { EditMortgageService } from './tpi-services/shared-services/edit-mortgage.service';
import { GisService } from './tpi-services/shared-services/gis.service';
import { PolicySearchResultsService } from './tpi-services/tpi-policy-inquiry/policy-search-results.service';
import { PolicySearchService } from './tpi-services/tpi-policy-inquiry/policy-search.service';
import { UtilsService } from './tpi-services/utility/utils-service.service';
import { DavinciWidgetService } from './tpi-services/davinci/davinci-widget.service';
import { PaginationSettings } from './tpi-components/manage-accounts-company-web/shared/pagination-settings/pagination-settings.service';
import { POLICY_REQUESTS_CONFIG, WINDOW_TOKEN } from './tokens/injection-tokens';
import { Title } from '@angular/platform-browser';

export const appConfig: ApplicationConfig = {
    providers: [
        provideZonelessChangeDetection(),
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        provideInternetServicingPolicyRequests(),
        provideAuth({
            config: {
                authority: environment.PING.authorizeUrl,
                authWellknownEndpointUrl: environment.PING.authWellknownEndpointUrl,
                redirectUrl: encodeURI(environment.PING.redirectUri),
                postLogoutRedirectUri: encodeURI(environment.PING.logoutUrl),
                clientId: environment.authorize.client_id,
                scope: 'openid nwapi',
                responseType: 'code',
                // Nationwide PING uses a different issuer domain vs API gateway domain
                issValidationOff: true,
                strictIssuerValidationOnWellKnownRetrievalOff: true,
                logLevel: LogLevel.Debug
            }
        }),
        provideStore({
            storeSearchFields: updateSearchFieldsReducer,
            storeSelectedCompanyFields: updateSelectedCompanyFieldsReducer,
            storeUserFields: updateUserFieldsReducer
        }),
        { provide: POLICY_REQUESTS_CONFIG, useValue: environment },
        { provide: 'policyRequestsConfig', useValue: environment },
        { provide: WINDOW_TOKEN, useValue: window },
        { provide: 'window', useValue: window },
        { provide: 'document', useValue: document },
        { provide: HTTP_INTERCEPTORS, useClass: RequestHeaderInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RequestTimeoutInterceptorService, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: RequestResponseLoggingInterceptorService, multi: true },
        { provide: ErrorHandler, useClass: ThirdPartyInquiryErrorHandler },
        APICommonService,
        DistributionPartnerInformationService,
        EcifService,
        GisService,
        DistributionPartnerIndividualInfoService,
        PolicySearchService,
        PolicySearchResultsService,
        EditLienholderService,
        EditMortgageService,
        PolicyNumberFormatterPipe,
        {
            provide: distributionPartnerInformationServiceConfigToken,
            useFactory: (): DistributionPartnerInformationServiceConfig => ({
                retrieveDistributionPartnerInfoUrl: environment.GETDISTRIBUTIONPARTNER_INFO.url,
                clientId: environment.authorize.client_id
            })
        },
        PaginationSettings,
        DavinciWidgetService,
        NgbActiveModal,
        UtilsService,
        SessionService,
        LoggerService,
        SplunkService,
        ValidatorService,
        Title,
        DatePipe
    ]
};